import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Search, Lock, EyeOff, Save, ShieldAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIS_C = { public: 'bg-emerald-100 text-emerald-700', registered: 'bg-blue-100 text-blue-700', paid: 'bg-violet-100 text-violet-700', internal: 'bg-gray-800 text-white' };
const EDIT_C = { supplier: 'Supplier-editable', locked: 'Viewable but locked', internal: 'Internal-invisible' };

const Governance = () => {
  const [policy, setPolicy] = useState(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);
  const [notes, setNotes] = useState('');
  const [signals, setSignals] = useState('');
  const [score, setScore] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { axios.get(`${API}/governance/field-policy`).then((r) => setPolicy(r.data)).catch(() => {}); }, []);

  const doSearch = useCallback(async () => {
    const { data } = await axios.get(`${API}/trust/suppliers`, { params: { search, limit: 25 } });
    setResults(data.items || []);
  }, [search]);
  useEffect(() => { doSearch(); }, [doSearch]);

  const open = async (s) => {
    const { data } = await axios.get(`${API}/governance/suppliers/${s.id}/internal`);
    setSel(data); setNotes(data.relationship_notes || ''); setSignals((data.intent_signals || []).join('\n')); setScore(data.intent_score ?? '');
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/governance/suppliers/${sel.id}/internal`, {
        relationship_notes: notes, intent_signals: signals.split('\n').filter(Boolean),
        intent_score: score === '' ? null : Number(score),
      });
      toast.success('Internal fields saved (never shown publicly)');
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="governance-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Field governance</h1>
        <p className="text-gray-500 text-sm mt-1">Visibility tiers and editability per field. Internal fields (relationship notes, intent signals) are structurally excluded from every public render.</p>
      </div>

      {policy && (
        <Card data-testid="field-policy"><CardContent className="p-5">
          <p className="text-sm font-semibold text-gray-900 mb-3">Field policy</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500 border-b border-gray-100"><th className="py-2">Field</th><th>Visibility</th><th>Editability</th></tr></thead>
              <tbody>
                {policy.fields.map((f) => (
                  <tr key={f.key} className="border-b border-gray-50" data-testid={`policy-row-${f.key}`}>
                    <td className="py-2 font-medium text-gray-800">{f.label}</td>
                    <td><Badge className={VIS_C[f.visibility]}>{f.visibility}</Badge></td>
                    <td className="text-gray-600">{EDIT_C[f.editability]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5 mt-3 inline-flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5" /> {policy.rule}</p>
        </CardContent></Card>
      )}

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        <div>
          <div className="flex gap-2 mb-3">
            <Input placeholder="Search suppliers" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="gov-search" />
            <Button variant="outline" onClick={doSearch} className="shrink-0"><Search className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
            {results.map((r) => (
              <button key={r.id} onClick={() => open(r)} className={`w-full text-left border rounded-lg px-3 py-2.5 text-sm transition-colors ${sel?.id === r.id ? 'border-[#00A79D] bg-[#00A79D]/5' : 'border-[#E5E7EB] hover:border-[#00A79D]/40'}`} data-testid={`gov-supplier-${r.id}`}>{r.name}</button>
            ))}
          </div>
        </div>

        <div>
          {!sel ? (
            <div className="border border-dashed border-gray-200 rounded-lg py-20 text-center text-gray-400" data-testid="gov-empty">Select a supplier to edit Internal fields.</div>
          ) : (
            <Card data-testid="internal-editor"><CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">{sel.name}</h2>
                <Badge className="bg-gray-800 text-white inline-flex items-center gap-1"><EyeOff className="w-3.5 h-3.5" /> Internal only</Badge>
              </div>
              <p className="text-xs text-gray-500 inline-flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> These fields never appear on the public site or in exports.</p>
              <div>
                <label className="text-sm font-semibold text-gray-900">Relationship notes</label>
                <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full mt-1 text-sm px-3 py-2 border border-[#E5E7EB] rounded-md" data-testid="internal-notes" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-900">Intent signals (one per line)</label>
                  <textarea rows={4} value={signals} onChange={(e) => setSignals(e.target.value)} className="w-full mt-1 text-sm px-3 py-2 border border-[#E5E7EB] rounded-md" data-testid="internal-signals" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-900">Intent score (0-100)</label>
                  <Input type="number" min="0" max="100" value={score} onChange={(e) => setScore(e.target.value)} className="mt-1" data-testid="internal-score" />
                </div>
              </div>
              <Button onClick={save} disabled={saving} className="bg-[#00A79D] hover:bg-[#00857D]" data-testid="internal-save">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save</Button>
            </CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Governance;
